import React, { useContext, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "AuthContextWrapper"
import { OUTCOMES } from "../../../constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { RenderFileUploadBox } from "views/fileCabinet/CreateOrEditUserDocument";
import { documentsAPI } from "apis";
import { CheckCircle } from "@mui/icons-material";
import { DOC_TYPE_ID_WILL, FileCabinetContext } from "FileCabinetContextWrapper";
import { Loading } from "views/shared/Loading";
import { EXECUTOR_VERIFICATION_STEP_WILL, EXECUTOR_VERIFICATION_SUBMITTED_ID, findMostRecentDocAndSrc } from ".";
import { ExecutorVerificationContext } from "ExecutorVerificationContextWrapper";

export const UploadCopyOfWillScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow } = useAuthContext()
  const { docs, docSrcs, loadingDocs, loadingSrcs, setDocs, setDocSrcs } = useContext(FileCabinetContext);
  const { verificationStepId, getStepStatusId, setStepStatusId, setVerificationStepId } = useContext(ExecutorVerificationContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<FileList | undefined>();
  const [errors, setErrors] = React.useState<string[]>([]);
  const [progress, setProgress] = React.useState<number | undefined>();
  const [previewImage, setPreviewImage] = React.useState<JSX.Element>(<img src="/static/images/Will_Sample.svg" style={{maxWidth:"100%",minHeight:"220px", maxHeight:"300px"}} />);
  const [previewImageKey, setPreviewImageKey] = React.useState<string>("initialKey");

  useEffect(() => {
    setVerificationStepId(EXECUTOR_VERIFICATION_STEP_WILL)
  }, [])

  const handleFileUpload = async (uploadedFiles: FileList): Promise<void> => {
    setUploadedFiles(uploadedFiles);
    setIsSubmitting(true);
    setProgress(undefined);
    const uploadFile = uploadedFiles?.item(0) || undefined;

    const result = await documentsAPI.saveDocument(
      "Copy of Will",
      undefined,
      DOC_TYPE_ID_WILL,
      uploadFile,
      "",
      undefined,
      (percent) => setProgress(percent), // callback to display progress,
      true
    );
    if (!result) {
      setErrors(["Sorry, file upload failed.  Please try again."]);
    }

    if(uploadFile && result && result?.version?.thumbnailS3Key) {
      setDocs([...docs, result])
      setDocSrcs([...docSrcs,URL.createObjectURL(uploadFile)])
    }

    setIsSubmitting(false);
  };

  const handleComplete = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen"); 

    setIsSubmitting(true)

    try {
      if(getStepStatusId(verificationStepId) !== EXECUTOR_VERIFICATION_SUBMITTED_ID) {
        await setStepStatusId(verificationStepId, EXECUTOR_VERIFICATION_SUBMITTED_ID)
      }
    }catch(error) {
      console.error("issue updating step, " + (error as any)?.message)
      setIsSubmitting(false)
      return false
    }
    
    setIsSubmitting(false)

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const handleBack = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    flow?.navigateToNext(currentScreen, OUTCOMES.back, navigate);
    return true;
  };

  
  useEffect(() => {
    // Preview image should be most recent doc, if any
    if(!loadingDocs && !loadingSrcs && docs && docs.length > 0 && docSrcs && docSrcs.length > 0) {
      const ret = findMostRecentDocAndSrc(docs, docSrcs, DOC_TYPE_ID_WILL)
      if(!ret) {
        return
      }

      setPreviewImage(<img src={ret[1]} style={{maxWidth:"100%",minHeight:"220px", maxHeight:"300px"}}  />)
      setPreviewImageKey(ret[1])
    }
  }, [loadingDocs, loadingSrcs, docs?.length, docSrcs?.length])


  if (!currentScreen) {
    return <></>;
  }


  return (
    <FlowScreenWrapper
      title="Please upload a copy of the latest valid will or codicil"
      subTitle={
        "Also referred to as the “last will and testament,” this legal document outlines how to distribute assets of the deceased after their passing. A codicil is a legal document that outlines any additions or modifications to the last will and testament."
      }
    >
      <div>
        {(loadingDocs || loadingSrcs) && <Loading />}
        {(!loadingDocs && !loadingSrcs) && <RenderFileUploadBox
          uploadedFiles={uploadedFiles}
          setUploadedFiles={handleFileUpload}
          setErrors={setErrors}
          previewImage={previewImage}
          key={previewImageKey}
        />}

        {(!errors || errors.length === 0) && progress && progress < 100 && (
          <Box sx={{ width: "100%" }} key={"newfileprogressbox"}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="secondary"
              sx={{
                height: 16,
                mt: 2,
                mb: 1,
              }}
            />
          </Box>
        )}
        {(!errors || errors.length === 0) && progress === 100 && (
          <Grid
            container
            alignContent={"center"}
            alignItems={"center"}
            justifyContent={"center"}
            style={{ width: "100%" }}
          >
            <Grid
              item
              alignContent={"center"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Typography
                variant={"body1"}
                component={"p"}
                color="secondary"
                style={{ marginRight: "10px", fontSize: "14pt" }}
              >
                Upload successful
              </Typography>
            </Grid>
            <Grid item>
              <CheckCircle fontSize="large" color="secondary" />
            </Grid>
          </Grid>
        )}
        {errors && errors.length > 0 && (
          <Typography variant={"body1"} color={"error"}>
            {errors.map((e) => (
              <>
                {e}
                <br />
              </>
            ))}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "space-between",
          }}
        >
          <Button
            color={"secondary"}
            variant="contained"
            disabled={isSubmitting}
            sx={{ minWidth: 180 }}
            onClick={async () => {
              setIsSubmitting(true);
              handleComplete();
              setIsSubmitting(false);
            }}
          >
            Continue
          </Button>
          <IconButton
            color={"secondary"}
            disabled={isSubmitting}
            aria-label="back"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon color={"secondary"} />
          </IconButton>
        </Box>
      </div>
    </FlowScreenWrapper>
  );
};
